import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, Save, X, ChevronDown, Search, Users, Plus, AlertTriangle } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import auditPlanService from '../../../../../../services/audit_services/auditPlanService';
import auditProgramService from '../../../../../../services/audit_services/auditProgramService';
import auditRiskService from '../../../../../../services/audit_services/auditRiskService';

export default function AuditPlanEdit() {
  const { id } = useParams();
  const { authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    objectives: '',
    scope: '',
    reference: '',
    program_id: '',
    program_title: '', // Store program title for display
    parties: [], // Users from audit program with their roles
    current_risks: [], // Current AuditPlanRisk records (for display and removal)
    new_risks_to_add: [], // New Risk records to be added (from search)
    location: '', // Separate location field
    note: '' // Separate note field
  });

  // Track risks to remove (AuditPlanRisk IDs)
  const [auditPlanRisksToRemove, setAuditPlanRisksToRemove] = useState(new Set());

  // Available programs
  const [programs, setPrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(true);

  // Program search
  const [programSearch, setProgramSearch] = useState({
    query: '',
    results: [],
    showDropdown: false,
    loading: false
  });

  // Available program parties (users from selected audit program)
  const [programParties, setProgramParties] = useState([]);
  const [loadingProgramParties, setLoadingProgramParties] = useState(false);

  // User search for parties from program
  const [partySearch, setPartySearch] = useState({
    query: '',
    results: [],
    showDropdown: false,
    loading: false
  });

  // Risk search
  const [riskSearch, setRiskSearch] = useState({
    query: '',
    results: [],
    showDropdown: false,
    loading: false
  });

  // Attachments
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [newAttachments, setNewAttachments] = useState([]);
  const [deletingAttachment, setDeletingAttachment] = useState(null);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchPrograms();
    fetchAuditPlanData();
  }, [id]);

  useEffect(() => {
    if (formData.program_id) {
      fetchProgramParties(formData.program_id);
    }
  }, [formData.program_id]);

  const fetchPrograms = async () => {
    try {
      const result = await auditProgramService.searchAuditPrograms(authTokens.access, '');
      
      if (result.success) {
        setPrograms(result.data);
      } else {
        console.error('Error fetching programs:', result.error);
        showError('Error loading programs');
      }
      setLoadingPrograms(false);
    } catch (error) {
      console.error('Error fetching programs:', error);
      showError('Error loading programs');
      setLoadingPrograms(false);
    }
  };

  const fetchProgramParties = async (programId) => {
    setLoadingProgramParties(true);
    try {
      const result = await auditProgramService.getProgramParties(authTokens.access, programId);
      
      if (result.success) {
        setProgramParties(result.data);
      } else {
        console.error('Error fetching program parties:', result.error);
        showError('Error loading program parties');
      }
      setLoadingProgramParties(false);
    } catch (error) {
      console.error('Error fetching program parties:', error);
      showError('Error loading program parties');
      setLoadingProgramParties(false);
    }
  };

  const fetchAuditPlanData = async () => {
    setInitialLoading(true);
    
    try {
      const result = await auditPlanService.getAuditPlanDetail(authTokens.access, id);
      
      if (result.success) {
        // Set program title for display and ensure arrays exist
        const planData = result.data;
        
        // Extract program_id from program object if it exists
        if (planData.program) {
          planData.program_id = planData.program.id;
          planData.program_title = planData.program.title;
        }
        
        // Ensure arrays exist to prevent undefined errors
        if (!planData.parties) {
          planData.parties = [];
        }
        if (!planData.risks) {
          planData.risks = [];
        }
        
        // Load existing attachments
        if (planData.attachments) {
          setExistingAttachments(planData.attachments);
        }
        
        // Normalize probability values for current risks
        const normalizedRisks = planData.risks.map(risk => ({
          ...risk,
          probability: normalizeProbabilityValue(risk.probability)
        }));
        
        // Set current risks (AuditPlanRisk records)
        setFormData({
          ...planData,
          current_risks: normalizedRisks,
          new_risks_to_add: []
        });
        
        // Load program parties if program exists
        if (planData.program_id) {
          fetchProgramParties(planData.program_id);
        }
      } else {
        console.error('Error fetching audit plan data:', result.error);
        showError(result.error || 'Error loading audit plan data');
      }
      setInitialLoading(false);
    } catch (error) {
      console.error('Error fetching audit plan data:', error);
      showError('Error loading audit plan data');
      setInitialLoading(false);
    }
  };

  // Helper function to normalize probability values
  const normalizeProbabilityValue = (probability) => {
    if (!probability) return 'medium';
    
    const probabilityMap = {
      'Mineur': 'low',
      'mineur': 'low',
      'low': 'low',
      'Low': 'low',
      'Normal': 'medium',
      'normal': 'medium',
      'medium': 'medium',
      'Medium': 'medium',
      'Majeur': 'high',
      'majeur': 'high',
      'high': 'high',
      'High': 'high',
      'Élevé': 'high',
      'élevé': 'high',
      'Critique': 'critical',
      'critique': 'critical',
      'critical': 'critical',
      'Critical': 'critical'
    };
    
    return probabilityMap[probability] || 'medium';
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Program search functionality
  const handleProgramSearch = async (query) => {
    setProgramSearch(prev => ({ ...prev, query, loading: true }));

    if (query.length >= 2) {
      try {
        const result = await auditProgramService.searchAuditPrograms(authTokens.access, query);
        
        if (result.success) {
          setProgramSearch(prev => ({
            ...prev,
            results: result.data,
            showDropdown: true,
            loading: false
          }));
        } else {
          console.error('Error searching programs:', result.error);
          setProgramSearch(prev => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error('Error searching programs:', error);
        setProgramSearch(prev => ({ ...prev, loading: false }));
      }
    } else {
      setProgramSearch(prev => ({
        ...prev,
        results: [],
        showDropdown: false,
        loading: false
      }));
    }
  };

  const selectProgram = (program) => {
    setFormData(prev => ({
      ...prev,
      program_id: program.id,
      program_title: program.title,
      parties: [] // Clear parties when program changes
    }));
    
    // Clear search
    setProgramSearch({
      query: '',
      results: [],
      showDropdown: false,
      loading: false
    });
  };

  // Party search functionality (from program parties)
  const handlePartySearch = async (query) => {
    setPartySearch(prev => ({ ...prev, query, loading: true }));

    if (query.length >= 2) {
      try {
        const filteredParties = programParties.filter(party =>
          party.name.toLowerCase().includes(query.toLowerCase()) ||
          party.email.toLowerCase().includes(query.toLowerCase()) ||
          party.partyRole.toLowerCase().includes(query.toLowerCase())
        );

        setPartySearch(prev => ({
          ...prev,
          results: filteredParties,
          showDropdown: true,
          loading: false
        }));
      } catch (error) {
        console.error('Error searching parties:', error);
        setPartySearch(prev => ({ ...prev, loading: false }));
      }
    } else {
      setPartySearch(prev => ({
        ...prev,
        results: [],
        showDropdown: false,
        loading: false
      }));
    }
  };

  // Risk search functionality
  const handleRiskSearch = async (query) => {
    setRiskSearch(prev => ({ ...prev, query, loading: true }));

    if (query.length >= 2) {
      try {
        const result = await auditRiskService.searchAuditRisks(authTokens.access, query);
        
        if (result.success) {
          setRiskSearch(prev => ({
            ...prev,
            results: result.data,
            showDropdown: true,
            loading: false
          }));
        } else {
          console.error('Error searching risks:', result.error);
          setRiskSearch(prev => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error('Error searching risks:', error);
        setRiskSearch(prev => ({ ...prev, loading: false }));
      }
    } else {
      setRiskSearch(prev => ({
        ...prev,
        results: [],
        showDropdown: false,
        loading: false
      }));
    }
  };

  const addParty = (party) => {
    // Check if party is already added
    if (!formData.parties || !formData.parties.find(p => p.id === party.id)) {
      setFormData(prev => ({
        ...prev,
        parties: [...(prev.parties || []), party]
      }));
    }
    
    // Clear search
    setPartySearch({
      query: '',
      results: [],
      showDropdown: false,
      loading: false
    });
  };

  const removeParty = (partyId) => {
    setFormData(prev => ({
      ...prev,
      parties: (prev.parties || []).filter(p => p.id !== partyId)
    }));
  };

  const addRisk = (risk) => {
    console.log('➕ ADDING NEW RISK (from Risk model):', risk);
    
    const newRisk = {
      ...risk,
      name: risk.name || risk.risk_name, // Normalize name field
      probability: 'medium' // Default probability
    };
    
    // Add to new risks list
    setFormData(prev => ({
      ...prev,
      new_risks_to_add: [...prev.new_risks_to_add, newRisk]
    }));
    
    // Clear search
    setRiskSearch({
      query: '',
      results: [],
      showDropdown: false,
      loading: false
    });
  };

  const removeCurrentRisk = (auditPlanRiskId) => {
    console.log('🗑️ REMOVING CURRENT RISK (AuditPlanRisk):', auditPlanRiskId);
    
    // Remove from current risks display
    setFormData(prev => ({
      ...prev,
      current_risks: prev.current_risks.filter(r => r.id !== auditPlanRiskId)
    }));
    
    // Add to removal list
    setAuditPlanRisksToRemove(prev => {
      const newSet = new Set([...prev, auditPlanRiskId]);
      console.log('🔍 Updated auditPlanRisksToRemove:', Array.from(newSet));
      return newSet;
    });
  };

  const removeNewRisk = (riskIndex) => {
    console.log('🗑️ REMOVING NEW RISK at index:', riskIndex);
    
    setFormData(prev => ({
      ...prev,
      new_risks_to_add: prev.new_risks_to_add.filter((_, index) => index !== riskIndex)
    }));
  };

  const updateCurrentRiskProbability = (riskId, probability) => {
    setFormData(prev => ({
      ...prev,
      current_risks: prev.current_risks.map(r => 
        r.id === riskId ? { ...r, probability } : r
      )
    }));
  };

  const updateNewRiskProbability = (riskIndex, probability) => {
    setFormData(prev => ({
      ...prev,
      new_risks_to_add: prev.new_risks_to_add.map((r, index) => 
        index === riskIndex ? { ...r, probability } : r
      )
    }));
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      auditor: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Auditor' },
      auditee: { bg: 'bg-green-100', text: 'text-green-800', label: 'Auditee' },
      participant: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Participant' },
      approver: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Approver' }
    };

    const config = roleConfig[role] || roleConfig.participant;
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (programSearch.showDropdown && !event.target.closest('.program-search-container')) {
        setProgramSearch(prev => ({ ...prev, showDropdown: false }));
      }
      if (partySearch.showDropdown && !event.target.closest('.party-search-container')) {
        setPartySearch(prev => ({ ...prev, showDropdown: false }));
      }
      if (riskSearch.showDropdown && !event.target.closest('.risk-search-container')) {
        setRiskSearch(prev => ({ ...prev, showDropdown: false }));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [programSearch.showDropdown, partySearch.showDropdown, riskSearch.showDropdown]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title || !formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.objectives || !formData.objectives.trim()) {
      newErrors.objectives = 'Objectives are required';
    }

    if (!formData.scope || !formData.scope.trim()) {
      newErrors.scope = 'Scope is required';
    }

    if (!formData.reference || !formData.reference.trim()) {
      newErrors.reference = 'Reference standards are required';
    }

    // For edit mode, we don't validate program_id since it's read-only
    // The program should already exist from the loaded data

    if (!formData.parties || formData.parties.length === 0) {
      newErrors.parties = 'At least one party is required';
    } else {
      // Validate that all parties have required fields
      const invalidParties = formData.parties.filter(p => !p.id);
      if (invalidParties.length > 0) {
        newErrors.parties = 'Some parties are missing required information';
      }
    }

    if (!formData.location || !formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    // Validate risks if they exist
    const totalRisks = (formData.current_risks?.length || 0) + (formData.new_risks_to_add?.length || 0);
    if (totalRisks > 0) {
      const invalidCurrentRisks = formData.current_risks?.filter(r => !r.id || !r.probability) || [];
      const invalidNewRisks = formData.new_risks_to_add?.filter(r => !r.id || !r.probability) || [];
      
      if (invalidCurrentRisks.length > 0 || invalidNewRisks.length > 0) {
        newErrors.risks = 'Some risks are missing required information';
      }
    }

    console.log('Validation errors:', newErrors);
    console.log('Validation errors:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showError('Please fix the errors before submitting');
      return;
    }

    setLoading(true);

    try {
      console.log('🔍 SUBMIT DEBUG - auditPlanRisksToRemove before service call:', Array.from(auditPlanRisksToRemove));
      
      const result = await auditPlanService.updateAuditPlan(
        authTokens.access, 
        id, 
        formData, 
        Array.from(auditPlanRisksToRemove)
      );
      
      if (result.success) {
        // Upload new attachments if any
        if (newAttachments.length > 0) {
          for (const file of newAttachments) {
            await auditPlanService.uploadAttachment(
              authTokens.access,
              id,
              file,
              ''
            );
          }
        }
        
        showSuccess('Audit plan updated successfully');
        navigate(`/dashboard/audit/plans/${id}`);
      } else {
        console.error('Error updating audit plan:', result.error);
        console.error('Error data:', result.errorData);
        showError(result.error || 'Error updating audit plan');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error updating audit plan:', error);
      showError('Error updating audit plan');
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setNewAttachments(prev => [...prev, ...files]);
  };

  const handleRemoveNewFile = (index) => {
    setNewAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteAttachment = async (attachmentId) => {
    setDeletingAttachment(attachmentId);
    const result = await auditPlanService.deleteAttachment(
      authTokens.access,
      attachmentId
    );
    
    if (result.success) {
      showSuccess('Pièce jointe supprimée');
      setExistingAttachments(prev => prev.filter(a => a.id !== attachmentId));
    } else {
      showError(result.error || 'Erreur lors de la suppression');
    }
    setDeletingAttachment(null);
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données du plan d'audit...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to={`/dashboard/audit/plans/${id}`}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Modifier le plan d'audit</h1>
          <p className="text-sm text-gray-600 mt-1">Mettre à jour les informations du plan d'audit</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Informations de base</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Title */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre de l'audit <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.title ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., Audit SI de l'année 2025"
              />
              {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
            </div>

            {/* Program - Read Only in Edit Mode */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Programme d'audit <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.program_title || 'No program selected'}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <span className="text-xs">Lecture seule</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                The audit program cannot be changed after creation. Create a new audit plan to use a different program.
              </p>
            </div>
          </div>

          {/* Objectives */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Objectifs <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.objectives}
              onChange={(e) => handleInputChange('objectives', e.target.value)}
              rows={3}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                errors.objectives ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Describe the objectives of this audit..."
            />
            {errors.objectives && <p className="text-red-500 text-sm mt-1">{errors.objectives}</p>}
          </div>

          {/* Scope */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Périmètre <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.scope}
              onChange={(e) => handleInputChange('scope', e.target.value)}
              rows={3}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                errors.scope ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Describe the scope of this audit..."
            />
            {errors.scope && <p className="text-red-500 text-sm mt-1">{errors.scope}</p>}
          </div>

          {/* Reference Standards */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Normes de référence <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.reference}
              onChange={(e) => handleInputChange('reference', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                errors.reference ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="e.g., ISO 27001, ISO 19011"
            />
            {errors.reference && <p className="text-red-500 text-sm mt-1">{errors.reference}</p>}
          </div>
        </div>

        {/* Parties Involved */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Parties impliquées</h3>
          
          {/* Parties actuelles */}
          {formData.parties && formData.parties.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 mb-3">Parties actuelles ({formData.parties.length})</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {formData.parties.map((party) => (
                  <div key={party.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-green-600">
                          {party.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{party.name}</p>
                        <p className="text-xs text-gray-500">{party.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getRoleBadge(party.partyRole)}
                      <button
                        type="button"
                        onClick={() => removeParty(party.id)}
                        className="p-1 text-red-600 hover:bg-red-100 rounded"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ajouter plus de parties */}
          {loadingProgramParties ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Chargement des parties disponibles...</p>
            </div>
          ) : (
            <>
              {/* Party Search */}
              <div className="party-search-container relative mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ajouter plus de parties du programme
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={partySearch.query}
                    onChange={(e) => handlePartySearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Rechercher des parties par nom, email ou rôle..."
                  />
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                </div>

                {/* Search Dropdown */}
                {partySearch.showDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {partySearch.loading ? (
                      <div className="p-3 text-center text-gray-500">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mx-auto"></div>
                        <span className="ml-2">Recherche...</span>
                      </div>
                    ) : partySearch.results.length > 0 ? (
                      partySearch.results.map((party) => (
                        <button
                          key={party.id}
                          type="button"
                          onClick={() => addParty(party)}
                          disabled={formData.parties && formData.parties.find(p => p.id === party.id)}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                            formData.parties && formData.parties.find(p => p.id === party.id) ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                <span className="text-xs font-medium text-gray-600">
                                  {party.name.split(' ').map(n => n[0]).join('')}
                                </span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{party.name}</p>
                                <p className="text-xs text-gray-500">{party.email}</p>
                              </div>
                            </div>
                            {getRoleBadge(party.partyRole)}
                          </div>
                        </button>
                      ))
                    ) : partySearch.query.length >= 2 ? (
                      <div className="p-3 text-center text-gray-500">Aucune partie trouvée</div>
                    ) : (
                      <div className="p-3 text-center text-gray-500">Tapez pour rechercher des parties...</div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          {errors.parties && <p className="text-red-500 text-sm mt-1">{errors.parties}</p>}
        </div>

        {/* Risks */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
              Risks
            </h3>
          </div>

          {/* Current Risks (AuditPlanRisk) */}
          {formData.current_risks && formData.current_risks.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 mb-3">Risques actuels ({formData.current_risks.length})</p>
              <div className="space-y-3">
                {formData.current_risks.map((risk) => (
                  <div key={`current-${risk.id}`} className="border border-green-200 rounded-lg p-4 bg-green-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{risk.name || risk.risk_name}</p>
                        <p className="text-xs text-gray-500 mt-1">{risk.description}</p>
                      </div>
                      <div className="flex items-center space-x-3 ml-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Probabilité</label>
                          <select
                            value={risk.probability}
                            onChange={(e) => updateCurrentRiskProbability(risk.id, e.target.value)}
                            className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          >
                            <option value="low">Mineur</option>
                            <option value="medium">Normal</option>
                            <option value="high">Majeur</option>
                            <option value="critical">Critique</option>
                          </select>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeCurrentRisk(risk.id)}
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New Risks to Add (from Risk model) */}
          {formData.new_risks_to_add && formData.new_risks_to_add.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 mb-3">Nouveaux risques à ajouter ({formData.new_risks_to_add.length})</p>
              <div className="space-y-3">
                {formData.new_risks_to_add.map((risk, index) => (
                  <div key={`new-${index}`} className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{risk.name || risk.risk_name}</p>
                        <p className="text-xs text-gray-500 mt-1">{risk.description}</p>
                      </div>
                      <div className="flex items-center space-x-3 ml-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Probabilité</label>
                          <select
                            value={risk.probability}
                            onChange={(e) => updateNewRiskProbability(index, e.target.value)}
                            className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="critical">Critical</option>
                          </select>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeNewRisk(index)}
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Risk Search */}
          <div className="risk-search-container relative mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ajouter plus de risques de la bibliothèque
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={riskSearch.query}
                onChange={(e) => handleRiskSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Rechercher des risques existants..."
              />
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>

            {/* Risk Search Dropdown */}
            {riskSearch.showDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {riskSearch.loading ? (
                  <div className="p-3 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mx-auto"></div>
                    <span className="ml-2">Recherche...</span>
                  </div>
                ) : riskSearch.results.length > 0 ? (
                  riskSearch.results.map((risk) => (
                    <button
                      key={risk.id}
                      type="button"
                      onClick={() => addRisk(risk)}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">{risk.name || risk.risk_name}</p>
                        <p className="text-xs text-gray-500">{risk.description}</p>
                      </div>
                    </button>
                  ))
                ) : riskSearch.query.length >= 2 ? (
                  <div className="p-3 text-center text-gray-500">Aucun risque trouvé</div>
                ) : (
                  <div className="p-3 text-center text-gray-500">Tapez pour rechercher des risques...</div>
                )}
              </div>
            )}
          </div>
          {errors.risks && <p className="text-red-500 text-sm mt-1">{errors.risks}</p>}
        </div>

        {/* Location and Note */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Localisation et notes</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Localisation <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.location ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="ex: Cameroun, France"
              />
              {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note
              </label>
              <textarea
                value={formData.note}
                onChange={(e) => handleInputChange('note', e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Notes supplémentaires sur l'audit..."
              />
            </div>
          </div>
        </div>

        {/* Attachments */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Pièces jointes</h3>
          
          {/* Existing attachments */}
          {existingAttachments.length > 0 && (
            <div className="mb-4 space-y-2">
              <p className="text-sm font-medium text-gray-700 mb-2">Pièces jointes existantes:</p>
              {existingAttachments.map((attachment) => (
                <div key={attachment.id} className="flex items-center justify-between bg-gray-50 p-3 rounded border">
                  <span className="text-sm text-gray-700">{attachment.file_name}</span>
                  <button
                    type="button"
                    onClick={() => handleDeleteAttachment(attachment.id)}
                    disabled={deletingAttachment === attachment.id}
                    className="text-red-600 hover:text-red-800 disabled:opacity-50"
                  >
                    {deletingAttachment === attachment.id ? (
                      <span className="text-xs">Suppression...</span>
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* New attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ajouter des pièces jointes
            </label>
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-lg file:border-0
                file:text-sm file:font-semibold
                file:bg-green-50 file:text-green-700
                hover:file:bg-green-100"
            />
            {newAttachments.length > 0 && (
              <div className="mt-2 space-y-2">
                <p className="text-xs text-gray-600">Nouvelles pièces jointes:</p>
                {newAttachments.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-blue-50 p-2 rounded">
                    <span className="text-sm text-gray-700">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveNewFile(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-4">
          <Link
            to={`/dashboard/audit/plans/${id}`}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Annuler
          </Link>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Mise à jour...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Mettre à jour le plan d'audit
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}